// Engine audio: RPM loop-bank player. Real recordings only; this file does no synthesis.
// Design: docs/superpowers/specs/2026-10-14-engine-range-game-design.md §11
import type { BankManifest, LoopDef } from '../types';
import { AUDIO_UNAVAILABLE, getAudioContext, loadBank } from './manifest';

/** Pitch-shift window per loop; the bank spacing keeps the true rpm inside it. */
const RATE_MIN = 0.8;
const RATE_MAX = 1.25;
/** Time constant for every parameter move (seconds); long enough to avoid zipper noise. */
const SMOOTH = 0.05;
/** Low-pass cutoff at zero load and at full load (Hz); interpolated exponentially. */
const CUTOFF_OFF_HZ = 1500;
const CUTOFF_ON_HZ = 18000;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

interface Loop {
  def: LoopDef;
  buffer: AudioBuffer;
  gain: GainNode;
  src: AudioBufferSourceNode | null;
}

/**
 * One engine. All loops run continuously once started; `set` moves the equal-power crossfade and the
 * playback rate so the two loops that bracket the true rpm carry the sound. Every method is a no-op
 * after `dispose()` or if the audio graph is unusable, so callers never need to guard.
 */
export class EngineVoice {
  private readonly ctx: AudioContext;
  private readonly loops: Loop[];
  private readonly startBuf: AudioBuffer | null;
  private readonly stopBuf: AudioBuffer | null;
  private readonly bus: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly master: GainNode;
  private buffers: Map<string, AudioBuffer> | null;
  private running = false;
  private disposed = false;
  private lastRpm: number;
  private lastLoad = 1;

  readonly manifest: BankManifest;

  private constructor(ctx: AudioContext, manifest: BankManifest, buffers: Map<string, AudioBuffer>) {
    this.ctx = ctx;
    this.manifest = manifest;
    this.buffers = buffers;
    this.master = ctx.createGain();
    this.master.gain.value = 1;
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.Q.value = 0.5;
    this.filter.frequency.value = CUTOFF_ON_HZ;
    this.bus = ctx.createGain();
    this.bus.gain.value = 0;
    this.bus.connect(this.filter);
    this.filter.connect(this.master);
    this.master.connect(ctx.destination);
    const defs = [...manifest.loops].sort((a, b) => a.rpm - b.rpm);
    this.loops = defs.map((def) => {
      const buffer = buffers.get(def.file);
      if (!buffer) throw new Error(`${AUDIO_UNAVAILABLE}: missing ${def.file}`);
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.bus);
      return { def, buffer, gain, src: null };
    });
    this.startBuf = manifest.start ? (buffers.get(manifest.start) ?? null) : null;
    this.stopBuf = manifest.stop ? (buffers.get(manifest.stop) ?? null) : null;
    this.lastRpm = this.loops[0].def.rpm;
  }

  /** Decode a bank and build the graph. Rejects with "Engine audio unavailable…" when it cannot. */
  static async load(bank: string, onProgress?: (p: number) => void): Promise<EngineVoice> {
    const ctx = getAudioContext();
    if (!ctx) throw new Error(`${AUDIO_UNAVAILABLE}: audio not unlocked`);
    const { manifest, buffers } = await loadBank(bank, onProgress);
    try {
      return new EngineVoice(ctx, manifest, buffers);
    } catch (e) {
      throw new Error(e instanceof Error && e.message.startsWith(AUDIO_UNAVAILABLE) ? e.message : AUDIO_UNAVAILABLE);
    }
  }

  private usable(): boolean {
    return !this.disposed && this.ctx.state !== 'closed';
  }

  private playOneShot(buffer: AudioBuffer, at: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.master);
    src.start(at);
  }

  /** Play the start one-shot (if any) and fade the loops in underneath it. Idempotent while running. */
  start(): void {
    if (!this.usable() || this.running) return;
    try {
      const now = this.ctx.currentTime;
      for (const l of this.loops) {
        const src = this.ctx.createBufferSource();
        src.buffer = l.buffer;
        src.loop = true;
        src.loopStart = 0;
        src.loopEnd = l.buffer.duration;
        src.connect(l.gain);
        src.start(now);
        l.src = src;
      }
      this.running = true;
      this.applyTargets(now);
      let fadeAt = now;
      let tau = 0.15;
      if (this.startBuf) {
        this.playOneShot(this.startBuf, now);
        // Bring the loops up under the last stretch of the start clip so the hand-over overlaps.
        fadeAt = now + Math.max(0, this.startBuf.duration - 1.5);
        tau = 0.5;
      }
      this.bus.gain.cancelScheduledValues(now);
      this.bus.gain.setValueAtTime(0, now);
      this.bus.gain.setTargetAtTime(1, fadeAt, tau);
    } catch {
      this.running = false;
    }
  }

  /** Drive the voice from the simulation: true engine rpm and load 0..1 (fraction of rated power). */
  set(rpm: number, load: number): void {
    if (!this.usable()) return;
    this.lastRpm = Number.isFinite(rpm) ? Math.max(0, rpm) : this.lastRpm;
    this.lastLoad = Number.isFinite(load) ? clamp(load, 0, 1) : this.lastLoad;
    if (this.running) this.applyTargets(this.ctx.currentTime);
  }

  private applyTargets(now: number): void {
    const rpm = this.lastRpm;
    const n = this.loops.length;
    let i = n - 1;
    for (let k = 0; k < n - 1; k++) {
      if (rpm < this.loops[k + 1].def.rpm) {
        i = k;
        break;
      }
    }
    const lo = this.loops[i];
    const hi = this.loops[Math.min(i + 1, n - 1)];
    const span = hi.def.rpm - lo.def.rpm;
    const t = span > 0 ? clamp((rpm - lo.def.rpm) / span, 0, 1) : 0;
    for (let k = 0; k < n; k++) {
      const l = this.loops[k];
      const g = k === i ? Math.cos((t * Math.PI) / 2) : k === i + 1 && hi !== lo ? Math.sin((t * Math.PI) / 2) : 0;
      l.gain.gain.setTargetAtTime(g, now, SMOOTH);
      l.src?.playbackRate.setTargetAtTime(clamp(rpm / l.def.rpm, RATE_MIN, RATE_MAX), now, SMOOTH);
    }
    const cutoff = CUTOFF_OFF_HZ * Math.pow(CUTOFF_ON_HZ / CUTOFF_OFF_HZ, this.lastLoad);
    this.filter.frequency.setTargetAtTime(cutoff, now, SMOOTH);
  }

  /** Fade the loops out, play the stop one-shot (if any) and release the loop sources. */
  stop(): void {
    if (!this.usable() || !this.running) return;
    this.running = false;
    try {
      const now = this.ctx.currentTime;
      const fade = 0.3;
      this.bus.gain.cancelScheduledValues(now);
      this.bus.gain.setValueAtTime(this.bus.gain.value, now);
      this.bus.gain.setTargetAtTime(0, now, fade / 3);
      if (this.stopBuf) this.playOneShot(this.stopBuf, now);
      for (const l of this.loops) {
        l.src?.stop(now + fade * 4);
        l.src = null;
      }
    } catch {
      // The graph is already silent or gone; nothing to release.
    }
  }

  /** Master volume 0..1. */
  setVolume(v: number): void {
    if (!this.usable()) return;
    this.master.gain.setTargetAtTime(clamp(Number.isFinite(v) ? v : 1, 0, 1), this.ctx.currentTime, SMOOTH);
  }

  /** Stop everything now, disconnect the graph and drop the decoded buffers so the GC can reclaim them. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.running = false;
    try {
      for (const l of this.loops) {
        try {
          l.src?.stop();
        } catch {
          // already stopped
        }
        l.src?.disconnect();
        l.src = null;
        l.gain.disconnect();
      }
      this.bus.disconnect();
      this.filter.disconnect();
      this.master.disconnect();
    } catch {
      // Disconnecting an already-detached node throws in some engines; the result is the same.
    }
    this.buffers?.clear();
    this.buffers = null;
  }
}
